import CloseButton from "Components/Common/CloseButton";
import CountrySelect from "Components/Common/CountrySelect";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import { Offcanvas, OffcanvasBody } from "reactstrap";
import AsyncSelect from "react-select/async";
import { fetchAgencies } from "services/api/collaboration";
import { CampaignsFilters } from "types/campaign";

interface CampaignListFiltersProps {
    isOpen: boolean,
    onClose: () => void,
    defaultFilters: CampaignsFilters,
    onApply: (filters: CampaignsFilters) => void,
}

const CampaignListFilters = ({ isOpen, onClose, defaultFilters, onApply }: CampaignListFiltersProps) => {
    const [filters, setFilters] = useState<CampaignsFilters>(defaultFilters);

    useEffect(() => {
        setFilters(defaultFilters);
    }, [defaultFilters]);

    const _loadAgenciesSuggestions = (query: string, callback: (options: { value: string, label: string, id?: number }[]) => void) => {
        if (!query || query.length < 3) {
            callback([]);
            return;
        }

        fetchAgencies(query)
            .then((resp) => callback(resp.map((item: { uuid: string, name: string, id?: number }) => ({
                value: item.uuid,
                label: item.name,
                id: item.id,
            }))))
            .catch(() => callback([]));
    };

    const loadAgenciesSuggestions = debounce(_loadAgenciesSuggestions, 200);

    const handleReset = () => {
        onApply({
            page: 1,
            status: defaultFilters.status,
        });
        onClose();
    };

    return (
        <Offcanvas isOpen={isOpen} toggle={onClose} direction="end">
            <CloseButton top="10px" onClose={onClose} />
            <OffcanvasBody className="p-0 position-relative">
                <div className="payments-offcanvas-bg" />
                <div className="filters p-3">
                    <div className="form-group filter-item">
                        <span className="filter-title">Agency</span>
                        <AsyncSelect
                            value={typeof filters.agency === "string" ? null : filters.agency ?? null}
                            onChange={(val) => {
                                setFilters({
                                    ...filters,
                                    agency: val ?? null,
                                    agency_id: val?.value,
                                });
                            }}
                            placeholder="Choose Agency"
                            menuPlacement="bottom"
                            cacheOptions
                            isClearable
                            loadOptions={loadAgenciesSuggestions}
                        />
                    </div>
                    <div className="form-group filter-item">
                        <label htmlFor="campaign-country" className="filter-title">Country</label>
                        <CountrySelect
                            value={filters.country || null}
                            onChange={(country) => setFilters({ ...filters, country: country ?? "" })}
                            placeholder="Select country"
                        />
                    </div>
                </div>
                <div className="filters-offcanvas-footer d-flex justify-content-between align-items-center px-3 p-4 gap-2">
                    <button type="button" className="theme-btn theme-btn-lg w-100 btn-primary-outlined" onClick={handleReset}>
                        Reset
                    </button>
                    <button
                        type="button"
                        className="theme-btn theme-btn-lg w-100 btn-primary"
                        onClick={() => {
                            onApply({ ...filters, page: 1 });
                            onClose();
                        }}
                    >
                        Apply
                    </button>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default CampaignListFilters;
